import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import type { FacilityNetworkViewModel } from "@utils/facilityNetworkView";
import {
  abbreviateFacilityType,
  facilityLocation,
  formatCcnNpiPair,
  networkRegistryDisplayIds,
} from "./facilityNetworkChildrenUtils";
import { isTopLevelParentView } from "./facilityNetworkHierarchyUtils";
import type { FacilityNetworkTabId } from "./FacilityNetworkTabBar";

export type FacilityNetworkChrome = "standalone" | "parent" | "child";

export type TypeBreakdownRow = {
  type: string;
  count: number;
};

export type NetworkCompositionCounts = {
  linkedCount: number;
  pendingCount: number;
  declinedCount: number;
  inviteCount: number;
};

/**
 * Priority CMS types for Overview breakdown (Figma 9086:67404).
 * Present priority types fill first; other types follow; UI caps at OVERVIEW_BREAKDOWN_MAX.
 */
export const OVERVIEW_TYPE_ORDER = ["SNF", "RHC", "FQHC", "CAH"] as const;

/** Max facility-type columns on Overview breakdown card. */
export const OVERVIEW_BREAKDOWN_MAX = 5;

export function compositionCountsFromRequestLists(params: {
  linkedCount: number;
  sent: Array<{ status: string }>;
  received: Array<{ status: string }>;
  openInboxCount: number;
}): NetworkCompositionCounts {
  const declinedCount = [...params.sent, ...params.received].filter(
    (row) => String(row.status || "").toLowerCase() === "declined",
  ).length;
  const inviteCount = params.sent.filter(
    (row) => String(row.status || "").toLowerCase() === "action_needed",
  ).length;
  return {
    linkedCount: params.linkedCount,
    pendingCount: params.openInboxCount,
    declinedCount,
    inviteCount,
  };
}

export function resolveFacilityNetworkChrome(
  view: FacilityNetworkViewModel | null,
): FacilityNetworkChrome | null {
  if (!view) return null;
  if (view.isStandalone) return "standalone";
  if (isTopLevelParentView(view)) return "parent";
  return "child";
}

export function collectLinkedMembers(
  view: FacilityNetworkViewModel,
): FacilityHierarchyInput[] {
  const selectedId = getFacilityHierarchyId(view.selected);
  return view.members.filter((member) => getFacilityHierarchyId(member) !== selectedId);
}

export function countDistinctFacilityTypes(
  facilities: FacilityHierarchyInput[],
): number {
  const types = new Set<string>();
  facilities.forEach((facility) => {
    const type = abbreviateFacilityType(facility);
    if (type !== "—") types.add(type);
  });
  return types.size;
}

export function breakdownByFacilityType(
  facilities: FacilityHierarchyInput[],
): TypeBreakdownRow[] {
  const counts = new Map<string, number>();
  facilities.forEach((facility) => {
    const type = abbreviateFacilityType(facility);
    if (type === "—") return;
    counts.set(type, (counts.get(type) || 0) + 1);
  });
  const orderIndex = (type: string) => {
    const idx = OVERVIEW_TYPE_ORDER.indexOf(
      type as (typeof OVERVIEW_TYPE_ORDER)[number],
    );
    return idx === -1 ? OVERVIEW_TYPE_ORDER.length : idx;
  };
  return Array.from(counts.entries())
    .map(([type, count]) => ({ type, count }))
    .filter((row) => row.count >= 1)
    .sort(
      (a, b) =>
        orderIndex(a.type) - orderIndex(b.type) || a.type.localeCompare(b.type),
    )
    .slice(0, OVERVIEW_BREAKDOWN_MAX);
}

export function getImmediateParent(
  view: FacilityNetworkViewModel,
): FacilityHierarchyInput | null {
  return view.ancestors[view.ancestors.length - 1] ?? null;
}

export function parentFacilityMetaLine(parent: FacilityHierarchyInput): string {
  const type = abbreviateFacilityType(parent);
  const { ccn, npi } = networkRegistryDisplayIds(parent);
  const location = facilityLocation(parent);
  return `${type} · ${formatCcnNpiPair(ccn, npi)} · ${location}`;
}

export function parentShortName(parent: FacilityHierarchyInput): string {
  const name = parent.facility_name?.trim() || "your parent";
  return name.split(/\s+/)[0] || "your parent";
}

export function formatShortSentLabel(iso?: string | null): string {
  if (!iso) return "Sent —";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Sent —";
  return `Sent ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

export function normalizeFacilityNetworkTab(
  tab: FacilityNetworkTabId,
): FacilityNetworkTabId {
  if (tab === "hierarchy") return "overview";
  if (tab === "link-request" || tab === "unlink-request") return "requests";
  return tab;
}

export function facilityExposureMeta(facility: FacilityHierarchyInput): string {
  const type = abbreviateFacilityType(facility);
  const { ccn, npi } = networkRegistryDisplayIds(facility);
  const location = facilityLocation(facility);
  return `${type} · ${formatCcnNpiPair(ccn, npi)} · ${location}`;
}
